import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { Field, field } from '../create/Field';
import { costFigure } from '../money';
import { mutationState } from '../ui/QueryStates';
import { useUnsentWrites } from '../ui/useConnection';
import { useFailedWriteFor } from '../../lib/failed-writes';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { PutBackButton } from './PutBackButton';
import { QtyStepper } from '../ui/QtyStepper';
import type { OnHandItem, StockLocation } from './types';
import { IconInventory, IconPlus } from '../icons';
import { useSession } from '../session';

/**
 * Parts on a job (PRD v2 §6.2) — the same card on the office's job page and in the tech's
 * hand, because it answers the same question in both places and a second copy would drift
 * within a month.
 *
 * The rule the shape enforces: stock is consumed at USE, never at quote. Nothing here
 * happens when a quote is written or a job is scheduled; it happens when someone says "I
 * put this in", which is the only moment the shop actually owns one fewer.
 *
 * ── The money boundary, and the sentence that keeps getting it wrong ──
 *
 * ⚠️ **The boundary is `what this customer owes` vs `what the shop paid` — never `money`
 * vs `no money`.** Every figure in this card is the second kind: unit cost, extended cost,
 * and the job's parts total are all the shop's cost basis, so `showCost={false}` removes
 * all of them. Nothing here is a price a customer is quoted.
 *
 * 🔴 **Do not read that as "techs don't see money."** `TechVisitPage` takes the payment,
 * the method, the cheque number and the **tip** — the Jobber-impossible wedge, argued for
 * in writing in `roles.ts`, which grants a tech the invoice that *reaches them through
 * their own assigned visit*. `apps/web/test/field-money.test.ts` pins both halves: the
 * cost basis is gated here, and the wedge's controls are asserted to still exist.
 *
 * ✅ **The server used to disagree; ticket 038 settled it.** `mcp/reads/customer.ts`'s
 * `showMoney = !tech` was and is **correct** — `customer_360` is browsing the ledger, which
 * `roles.ts` withholds. `mcp/reads/schedule.ts` had copied that doctrine onto **`get_job`**,
 * which is already gated by `techWorksJob`, and returned `payment_refs: []` to the tech who
 * recorded the payment sixty seconds earlier. `get_job` now gives a tech the invoice
 * (`roles.ts` grants exactly that through their own assigned visit) and the payments **they
 * recorded**; anyone else's are named in the response's `withheld` array rather than
 * dropped. Do not "fix" the remaining asymmetry by deleting anything on this side.
 */
export function PartsUsedCard({
  jobId,
  visitId,
  showCost,
  /** Techs may only pull from their own van, so they never see a location picker. */
  lockToMyVan = false,
  closedReason,
}: {
  jobId: string;
  /**
   * The visit the tech has open, sent with every part used here, or null on the office's job
   * page (Inventory v2 Phase 1, F1): the server stores it, and null reads "Visit not recorded".
   * From the screen, never the device (ADR 0001). Required, like `closedReason`, so every
   * screen that mounts the card says which it is.
   */
  visitId: string | null;
  /**
   * Why this job takes no more parts (`partsClosedReason(job)`), or null while it does. Shown in
   * place of "Add a part"; the server refuses a use with the same sentence. Required, like
   * `showCost`, so every screen that mounts the card answers it.
   */
  closedReason: string | null;
  /**
   * Whether to render the shop's cost basis. **Required on purpose — there is no default
   * to reach for.** A default of `true` means the next screen that mounts this card leaks
   * the cost basis by saying nothing, which is the whole defect this prop exists to close;
   * a default of `false` silently blanks the office's own numbers. The compiler makes each
   * call site answer.
   *
   * This hides the figures; it is not what keeps them off the phone. The server does that
   * (build-map 047, `apps/api/src/trpc/cost-boundary.ts`): `partsOnJob`, `onHand`,
   * `useOnJob` and `lowStock` answer a caller without `report:read` with every cost figure
   * removed, so a tech's persisted cache never holds one. The office reads the same
   * procedures, through the same cache keys, in full. Keep the prop anyway: it is the
   * screen's own statement of who it is for.
   */
  showCost: boolean;
  lockToMyVan?: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const parts = useQuery(trpc.inventory.partsOnJob.queryOptions({ jobId }));
  const [adding, setAdding] = useState(false);
  const [warning, setWarning] = useState<{
    tone: 'warn' | 'success';
    text: string;
  } | null>(null);
  // The part being put back, by its cost id: the sheet reads the row from the live list, so a
  // count refreshed after someone else put some back is the one it shows.
  const [puttingBack, setPuttingBack] = useState<string | null>(null);

  const rows = parts.data ?? [];
  // Parts used here with no signal: still in the queue, so the server has no row for them yet.
  // Drawn from the queue rather than written into the list's cache, so one the server refuses
  // leaves the card on the next render, and the unsent-work panel says why.
  const waiting = useUnsentWrites('inventory.useOnJob', jobId, 'jobId');
  // A queued use carries an item id and no name: the parts on hand name it, from the cache
  // the Add a part sheet filled, so offline it reads what is already on the phone.
  const onHand = useQuery({
    ...trpc.inventory.onHand.queryOptions(),
    enabled: waiting.length > 0,
  });
  const nameOf = (itemId: unknown) =>
    (onHand.data?.items as OnHandItem[] | undefined)?.find((i) => i.id === itemId)
      ?.name ?? 'A part';
  const listed = rows.length + waiting.length;
  const backRow = rows.find((p) => p.jobCostId === puttingBack) ?? null;
  // Absent for a caller without `report:read` (build-map 047); the total shows only with
  // `showCost`, which is exactly the callers who receive it.
  const totalCents = rows.reduce((sum, p) => sum + (p.totalCostCents ?? 0), 0);

  const body = parts.isPending ? (
    <p className="text-sm text-muted-foreground">Loading…</p>
  ) : parts.isError ? (
    <p className="text-sm text-[var(--sw-danger-text)]">
      Couldn't load the parts on this job.{' '}
      <button type="button" className="underline" onClick={() => void parts.refetch()}>
        Try again
      </button>
    </p>
  ) : listed === 0 && closedReason ? null : listed === 0 ? (
    <p className="text-sm text-muted-foreground">
      Nothing off the truck yet. Add a part when you put it in — that's what takes it off
      your van and records it against this job.
    </p>
  ) : (
    <ul className="divide-y divide-border text-sm">
      {/* Newest first, as the server orders the rest: nothing it has answered is newer. */}
      {waiting.map((w) => (
        <li key={String(w.mutationId)} className="flex items-center gap-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{nameOf(w.itemId)}</p>
            <p className="text-xs text-muted-foreground">
              {formatQty(Math.round(Number(w.qty) * 100))} · Waiting to send
            </p>
          </div>
        </li>
      ))}
      {rows.map((p) => (
        <li key={p.jobCostId} className="flex items-center gap-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{p.name}</p>
            <p className="text-xs text-muted-foreground">
              {formatQty(Math.round(Number(p.qty) * 100))} from {p.locationName}
              {!showCost ? null : p.unitCostCents === 0 ? (
                // Never dress a missing cost as free — it's a part nobody has priced yet.
                <span className="text-[var(--sw-warn-text)]"> · no cost on file</span>
              ) : (
                // ⚠️ A FRAGMENT, not a template literal. `costFigure` returns `string |
                // null`, and `${null}` stringifies to the four visible characters "null" —
                // so the outer guard was the only thing standing between a tech and
                // " · null each". In JSX child position a null renders nothing.
                <> · {costFigure(p.unitCostCents, showCost)} each</>
              )}
            </p>
          </div>
          {/* "No cost on file" is itself a statement about the shop's books, so it goes
              with the numbers rather than staying behind as a hint. */}
          {showCost ? (
            // Keyed off the value it DISPLAYS. Keyed off `unitCostCents` it drew an
            // em-dash over a real extended cost that `totalCents` was still summing, so
            // the rows and the footer disagreed.
            <span className="shrink-0 tabular-nums">
              {p.totalCostCents === 0 ? '—' : costFigure(p.totalCostCents, showCost)}
            </span>
          ) : null}
          {/* Locked while a return of THIS part waits in the offline queue: a second press
              would queue a second partial return under a new key, and both would land. */}
          <PutBackButton
            cost={{ id: p.jobCostId, description: p.name }}
            listBusy={false}
            label="Put back"
            onPress={() => setPuttingBack(p.jobCostId)}
          />
        </li>
      ))}
    </ul>
  );

  const title = listed === 0 ? 'Parts used' : `Parts used (${listed})`;

  return (
    <SectionCard
      title={title}
      right={
        closedReason ? null : (
          <Button variant="ghost" onClick={() => setAdding(true)}>
            <IconPlus size={14} />
            Add a part
          </Button>
        )
      }
    >
      {closedReason ? <Notice tone="neutral">{closedReason}</Notice> : null}
      {warning ? <Notice tone={warning.tone}>{warning.text}</Notice> : null}
      {body}
      {showCost && rows.length > 0 && totalCents > 0 ? (
        <p className="mt-3 text-right text-sm">
          <span className="text-muted-foreground">Parts on this job </span>
          <strong className="tabular-nums">{costFigure(totalCents, showCost)}</strong>
        </p>
      ) : null}

      {adding ? (
        <UsePartSheet
          jobId={jobId}
          visitId={visitId}
          showCost={showCost}
          lockToMyVan={lockToMyVan}
          onClose={() => setAdding(false)}
          onWarn={(text) => setWarning(text ? { tone: 'warn', text } : null)}
        />
      ) : null}

      {backRow ? (
        <PutBackSheet
          part={backRow}
          onClose={() => setPuttingBack(null)}
          onDone={(text) => {
            setWarning({ tone: 'success', text });
            setPuttingBack(null);
          }}
        />
      ) : null}
    </SectionCard>
  );
}

/**
 * Adding a part is two decisions at most: which one, and how many. Where it came from is
 * pre-answered with the van and only shown when there's a real choice to make.
 */
export function UsePartSheet({
  jobId,
  visitId,
  showCost,
  lockToMyVan,
  onClose,
  onWarn,
}: {
  jobId: string;
  visitId: string | null;
  /** Same boundary as the card: `onHand` carries `avgCostCents` only to callers allowed it (047). */
  showCost: boolean;
  lockToMyVan: boolean;
  onClose: () => void;
  onWarn: (message: string | null) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const role = useSession().role;
  const fieldPlan = useQuery({
    ...trpc.jobParts.forVisit.queryOptions({ visitId: visitId ?? '' }),
    enabled: Boolean(visitId) && lockToMyVan,
  });
  const officePlan = useQuery({
    ...trpc.jobParts.list.queryOptions({ jobId }),
    enabled: !lockToMyVan && can(role, 'job', 'update'),
  });
  const [chosenLine, setChosenLine] = useState<{ itemId: string; lineId: string } | null>(
    null,
  );
  const onHand = useQuery(trpc.inventory.onHand.queryOptions());
  const myVan = useQuery(trpc.inventory.myVan.queryOptions());
  const [search, setSearch] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const [qty, setQty] = useState('1');
  const [locationId, setLocationId] = useState<string | null>(null);

  const items = (onHand.data?.items ?? []) as OnHandItem[];
  const locations = (onHand.data?.locations ?? []) as StockLocation[];
  // The van is the answer nearly every time, so it's pre-selected rather than asked for.
  const sourceId = locationId ?? myVan.data?.id ?? locations[0]?.id ?? null;

  /**
   * One use per tap: a second is a second part on the job, billed twice once the office adds
   * both, under a key of its own the server cannot dedupe. `disabled` cannot stop it (both taps land
   * in one render's handler, `useTapOnce`); the lock holds until the use settles, through a
   * wait in the queue.
   */
  const lock = useTapOnce();
  const use = useMutation(
    trpc.inventory.useOnJob.mutationOptions({
      onSettled: () => lock.release(),
      onSuccess: (r) => {
        onWarn(
          r.wentNegative
            ? `Recorded. That puts ${itemName(r.locationId)} below zero — count it when you get a chance.`
            : null,
        );
        void qc.invalidateQueries();
        onClose();
      },
    }),
  );

  const itemName = (id: string) =>
    locations.find((l) => l.id === id)?.name ?? 'that location';

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const scored = items.filter(
      (i) =>
        !q || i.name.toLowerCase().includes(q) || (i.sku ?? '').toLowerCase().includes(q),
    );
    return scored.slice(0, 40);
  }, [items, search]);

  const selected = items.find((i) => i.id === itemId) ?? null;
  const plan = lockToMyVan ? fieldPlan : officePlan;
  const scopedLines = (plan.data?.lines ?? []).filter(
    (line) => line.item.id === itemId && line.workOrderLineId,
  );
  const workOrderLineId =
    chosenLine?.itemId === itemId
      ? chosenLine.lineId
      : scopedLines.length === 1
        ? scopedLines[0]!.workOrderLineId
        : undefined;
  const requiresChoice =
    scopedLines.length > 1 &&
    !scopedLines.some((line) => line.workOrderLineId === workOrderLineId);
  const atSource =
    selected && sourceId
      ? (selected.cells.find((c) => c.locationId === sourceId)?.ownHundredths ?? 0)
      : 0;
  const wanted = Math.round(Number(qty) * 100);
  /**
   * On the tech screen the source is always their own van, and the server 409s when they
   * don't have one. The van query has already answered by the time this renders, so the
   * button is disabled with the reason shown rather than left tappable and refused — a
   * control that can only fail is worse than no control.
   */
  const noVan = lockToMyVan && !myVan.isPending && !myVan.data?.id;
  const canSave =
    Boolean(selected) && wanted > 0 && !use.isPending && !noVan && !requiresChoice;
  // Waiting for a signal is not saving: the part shows on the card as waiting to send.
  const phase = mutationState(use);

  return (
    <Sheet
      open
      onClose={onClose}
      title="Add a part"
      footer={
        <Button
          className="w-full"
          disabled={!canSave}
          onClick={() =>
            sendOnce(lock, use.mutate, {
              jobId,
              ...(visitId ? { visitId } : {}),
              itemId: selected!.id,
              ...(workOrderLineId ? { workOrderLineId } : {}),
              ...(lockToMyVan || !sourceId ? {} : { locationId: sourceId }),
              qty: Number(qty),
            })
          }
        >
          {phase === 'queued'
            ? 'Waiting to send'
            : use.isPending
              ? 'Saving…'
              : 'Use it on this job'}
        </Button>
      }
    >
      <div className="space-y-3">
        {use.error ? <Notice tone="danger">{use.error.message}</Notice> : null}
        {noVan ? (
          <Notice tone="warn">
            You don't have a van set up yet, so there's nowhere to take parts from. Ask
            the office to add one — then what you use here comes off it automatically.
          </Notice>
        ) : null}

        {selected ? (
          <>
            <div className="rounded-[var(--radius)] border border-border bg-surface-2 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">{selected.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatQty(atSource)} on{' '}
                    {sourceId ? itemName(sourceId) : 'no location picked'}
                    {!showCost ? null : selected.avgCostCents == null ? (
                      ' · no cost on file yet'
                    ) : (
                      <> · {costFigure(selected.avgCostCents, showCost)} each</>
                    )}
                  </p>
                </div>
                <Button variant="ghost" onClick={() => setItemId(null)}>
                  Change
                </Button>
              </div>
            </div>

            <Field label="How many">
              <input
                className={field}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="decimal"
                autoFocus
              />
            </Field>

            {scopedLines.length > 0 ? (
              <Field label="Work order task / material line">
                <select
                  className={field}
                  value={workOrderLineId ?? ''}
                  onChange={(event) =>
                    setChosenLine({ itemId: selected.id, lineId: event.target.value })
                  }
                >
                  <option value="">Choose the task this part was used for</option>
                  {scopedLines.map((line) => (
                    <option key={line.workOrderLineId} value={line.workOrderLineId}>
                      {line.taskName || 'General work'} · {line.item.name} ·{' '}
                      {formatQty(line.leftHundredths)} left
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {plan.isError || plan.isPaused ? (
              <Notice tone="warn">
                Task allocation details could not refresh. Actual use is validated against
                the work order when it reaches the server.
                <Button variant="ghost" size="sm" onClick={() => void plan.refetch()}>
                  Refresh task lines
                </Button>
              </Notice>
            ) : null}

            {/* Only the office ever sees this — a tech's answer is always their own van. */}
            {lockToMyVan ? null : (
              <Field label="Taken from">
                <select
                  className={field}
                  value={sourceId ?? ''}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {' '.repeat(l.depth * 2)}
                      {l.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {wanted > atSource ? (
              <Notice tone="warn">
                The books only show {formatQty(atSource)} there. Recording it anyway is
                fine — the part is in the customer's equipment either way, and it'll show
                up in the office's reconcile list.
              </Notice>
            ) : null}
          </>
        ) : (
          <>
            <input
              className={field}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search parts"
              aria-label="Search parts"
              autoFocus
            />
            {matches.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {items.length === 0
                  ? "You're not counting any parts yet. Turn on tracking for a part in Inventory first."
                  : 'Nothing matches that.'}
              </p>
            ) : (
              <ul className="max-h-[50vh] divide-y divide-border overflow-y-auto text-sm">
                {matches.map((i) => {
                  const here = sourceId
                    ? (i.cells.find((c) => c.locationId === sourceId)?.ownHundredths ?? 0)
                    : 0;
                  return (
                    <li key={i.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-surface-2"
                        onClick={() => setItemId(i.id)}
                      >
                        <IconInventory
                          size={15}
                          className="shrink-0 text-muted-foreground"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{i.name}</span>
                          {i.sku ? (
                            <span className="block text-xs text-muted-foreground">
                              {i.sku}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={`shrink-0 text-xs tabular-nums ${
                            here <= 0 ? 'text-muted-foreground' : ''
                          }`}
                        >
                          {formatQty(here)} on hand
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

/**
 * Hundredths that can still be put back: `qty` on the job, less the returns still in the
 * queue. A queued return with no quantity takes all that was left.
 */
export function leftToPutBack(
  qty: string,
  queued: readonly Record<string, unknown>[],
): number {
  return queued.reduce<number>(
    (left, w) =>
      w.qty === undefined ? 0 : Math.max(0, left - Math.round(Number(w.qty) * 100)),
    Math.round(Number(qty) * 100),
  );
}

/** "3 stay" / "1 stays": what is left on the job after a partial return. */
function stayLeft(hundredths: number): string {
  return `${formatQty(hundredths)} ${hundredths === 100 ? 'stays' : 'stay'}`;
}

/** The words on the Put back button: all of it, or how many come back and how many stay. */
export function putBackLabel(backHundredths: number, usedHundredths: number): string {
  if (backHundredths >= usedHundredths) {
    return usedHundredths === 100
      ? 'Put it back'
      : `Put all ${formatQty(usedHundredths)} back`;
  }
  return `Put ${formatQty(backHundredths)} back · ${stayLeft(usedHundredths - backHundredths)}`;
}

/**
 * Putting a part back (Inventory v2 ticket 04, prototype verdict A): a sheet whose stepper
 * starts at ALL of it, so the old one-tap full return is one more tap, and fewer leaves the rest
 * on the job. The server decides what remains; a "more than remains" refusal (someone else put
 * some back meanwhile) is shown here, and the count above it refreshes.
 */
export function PutBackSheet({
  part,
  onClose,
  onDone,
}: {
  part: { jobCostId: string; name: string; qty: string; locationName: string };
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  // One return per tap: a second, under its own key, is a second partial return (`useTapOnce`).
  const lock = useTapOnce();
  const putBack = useMutation(
    trpc.inventory.returnFromJob.mutationOptions({
      onSettled: () => lock.release(),
      // Worded from what was SENT: the sheet may have been edited while a queued return waited.
      onSuccess: (_result, sent) => {
        void qc.invalidateQueries();
        const sentBack = Math.round(Number(sent.qty ?? used / 100) * 100);
        onDone(
          sentBack >= used
            ? `${part.name} is back on ${part.locationName}.`
            : `Put ${formatQty(sentBack)} back on ${part.locationName}. ${stayLeft(used - sentBack)} on this job.`,
        );
      },
      // The count may have moved under us: show the refusal, and ask for the list again.
      onError: () => void qc.invalidateQueries(trpc.inventory.partsOnJob.pathFilter()),
    }),
  );
  /**
   * What can still come back: what is on the job, less any return of it still on the phone
   * from an earlier press (the server has not taken it off yet). This sheet's own return is
   * not counted against itself. Read from the queue, never `setQueryData` (Phase 1).
   */
  const others = useUnsentWrites('inventory.returnFromJob', part.jobCostId, 'jobCostId');
  const used = leftToPutBack(
    part.qty,
    others.filter((w) => w.mutationId !== putBack.variables?.mutationId),
  );
  const [qty, setQty] = useState(formatQty(used));
  // A refusal refreshes the list; when what is on the job changes, start again from all of it.
  useEffect(() => setQty(formatQty(used)), [used]);
  const back = Math.round(Number(qty) * 100);
  const valid = Number.isFinite(back) && back > 0 && back <= used;

  // Queued offline or on its way: the amount is fixed, so the controls stop taking edits.
  const phase = mutationState(putBack);
  const locked = putBack.isPending;
  // Refused and logged while the sheet was open: it is retried from Changes not sent with its
  // ORIGINAL key, so a press here (a new key) would be a second return (PutBackButton's lock).
  const refused = useFailedWriteFor(
    'inventory.returnFromJob',
    part.jobCostId,
    'jobCostId',
  );

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Put back: ${part.name}`}
      footer={
        <Button
          className="w-full"
          disabled={!valid || putBack.isPending || Boolean(refused)}
          onClick={() =>
            sendOnce(lock, putBack.mutate, { jobCostId: part.jobCostId, qty: back / 100 })
          }
        >
          {refused
            ? 'Not sent'
            : phase === 'queued'
              ? 'Waiting to send'
              : phase === 'sending'
                ? 'Putting back…'
                : putBackLabel(valid ? back : used, used)}
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {formatQty(used)} used on this job · goes back on {part.locationName}
        </p>
        {putBack.error ? <Notice tone="danger">{putBack.error.message}</Notice> : null}
        {used > 100 || used % 100 !== 0 ? (
          <>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <QtyStepper
                value={qty}
                onChange={setQty}
                max={used}
                locked={locked}
                label="How many to put back"
              />
              <Button
                variant="ghost"
                aria-pressed={valid && back >= used}
                disabled={locked}
                onClick={() => setQty(formatQty(used))}
              >
                All {formatQty(used)}
              </Button>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {!valid
                ? `Pick between 0.01 and ${formatQty(used)}.`
                : back >= used
                  ? 'All of it comes off this job.'
                  : `${stayLeft(used - back)} on this job.`}
            </p>
          </>
        ) : null}
      </div>
    </Sheet>
  );
}
